/**
 * Workshop BoardFrame — mini board snapshot (header + compact kanban columns).
 *
 * Production reference: kanban recipes copied from
 * frontend/src/components/shapes/taskboard-frame/kanban-card.tsx and
 * task-metadata-chips.tsx. The PRIORITY_BORDER_CLASSES strings below are
 * copied verbatim (never imported — workshop must not depend on production
 * shape code). Card layout follows the kanban-card recipe: compact card with
 * left priority border, title line, and muted meta line. Column dots follow
 * the production STATUS_DOT_COLORS recipe (warning / status-selected /
 * accent var tokens); dotColor props below use those same tokens.
 *
 * Card clicks call `onCardClick` when provided.
 */

import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BoardCard {
  id: string;
  title: string;
  meta?: string;
}

interface BoardColumn {
  id: string;
  label: string;
  dotColor: string;
  count?: number;
  cards: BoardCard[];
}

interface BoardFrameProps {
  title: string;
  path?: string;
  columns: BoardColumn[];
  status?: FrameStatus;
  onCardClick?: (cardId: string) => void;
  className?: string;
  "data-testid"?: string;
}

// ─── Mock data ────────────────────────────────────────────────────────────────

// Copied verbatim from task-metadata-chips.tsx PRIORITY_BORDER_CLASSES
// (mock-only rotation below gives each card a production border token).
const BOARD_PRIORITY_BORDERS = [
  "border-l-warning",
  "border-l-status-selected",
  "border-l-muted",
  "border-l-danger",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function borderForCard(columnIndex: number, cardIndex: number): string {
  return BOARD_PRIORITY_BORDERS[(columnIndex + cardIndex) % BOARD_PRIORITY_BORDERS.length] ?? "border-l-muted";
}

// ─── Main component ───────────────────────────────────────────────────────────

export function BoardFrame({
  title,
  path,
  columns,
  status = "done",
  onCardClick,
  className,
  "data-testid": testId = "board-frame",
}: BoardFrameProps) {
  const handleCardClick = (e: React.MouseEvent, card: BoardCard) => {
    e.stopPropagation();
    onCardClick?.(card.id);
  };

  return (
    <div
      className={cn(surfaceRoleClassName("frame"), "group relative flex flex-col overflow-hidden rounded-frame", className)}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-2.5 px-3 py-2.5">
        <div
          className="flex items-center gap-2"
          data-testid={`${testId}-header`}
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-canvas-base font-semibold leading-snug text-primary" data-testid={`${testId}-title`}>
              {title}
            </p>
            {path && (
              <p className="mt-0.5 truncate text-canvas-xs text-muted" data-testid={`${testId}-path`}>
                {path}
              </p>
            )}
          </div>
          <span
            className="shrink-0 rounded-pill bg-elevated/60 px-2 py-0.5 text-canvas-2xs font-medium text-secondary"
            data-testid={`${testId}-kind-pill`}
          >
            Board
          </span>
        </div>

        {status === "error" ? (
          <div className="flex items-center gap-2 py-3" data-testid={`${testId}-error`}>
            <Icons.CircleAlert className="h-3.5 w-3.5 shrink-0 text-status-error" aria-hidden="true" />
            <span className="text-canvas-base text-muted">Couldn&apos;t load board. Try again.</span>
          </div>
        ) : status === "streaming" ? (
          <div className="flex items-center gap-2 py-3" data-testid={`${testId}-loading`}>
            <Spinner className="h-3 w-3" />
            <span className="text-canvas-base text-muted">Loading board…</span>
          </div>
        ) : columns.length === 0 ? (
          <p className="py-3 text-center text-canvas-base italic text-muted" data-testid={`${testId}-empty`}>
            No columns yet
          </p>
        ) : (
          <div
            className="nodrag flex min-h-0 flex-1 gap-2 overflow-auto"
            data-testid={`${testId}-columns`}
            onClick={(e) => e.stopPropagation()}
          >
            {columns.map((column, columnIndex) => {
              const count = column.count ?? column.cards.length;
              return (
                <div
                  key={column.id}
                  className="flex w-[150px] shrink-0 flex-col gap-1.5"
                  data-testid={`${testId}-column-${column.id}`}
                >
                  <div className="flex items-center gap-1.5" data-testid={`${testId}-column-header-${column.id}`}>
                    <span
                      className={cn("h-1.5 w-1.5 shrink-0 rounded-full", column.dotColor)}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate text-canvas-xs font-medium text-secondary">
                      {column.label}
                    </span>
                    <span className="shrink-0 font-mono text-canvas-2xs text-muted">
                      {count}
                    </span>
                  </div>

                  {column.cards.length === 0 ? (
                    <p
                      className="rounded-control bg-elevated/60 px-2 py-1.5 text-center text-canvas-2xs italic text-muted"
                      data-testid={`${testId}-column-empty-${column.id}`}
                    >
                      No cards
                    </p>
                  ) : (
                    column.cards.map((card, cardIndex) => (
                      <button
                        key={card.id}
                        type="button"
                        onClick={(e) => handleCardClick(e, card)}
                        data-testid={`${testId}-card-${card.id}`}
                        className={cn(
                          "rounded border-l-2 bg-elevated/60 px-2 py-1.5 text-left transition-colors",
                          borderForCard(columnIndex, cardIndex),
                          "hover:bg-elevated",
                        )}
                      >
                        <p className="truncate text-canvas-xs font-medium leading-snug text-primary">
                          {card.title}
                        </p>
                        {card.meta && (
                          <p className="mt-0.5 truncate text-canvas-2xs text-muted">
                            {card.meta}
                          </p>
                        )}
                      </button>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

export type { BoardFrameProps, BoardColumn, BoardCard };
